// Active-learning card — surfaces the edges whose DIRECTION the data
// couldn't decide (Markov-equivalent / low orientation stability), ranked by
// how much confirming them would sharpen the rest of the graph. Backend
// supplies up to 5 in `discovery.suggested_labels`; `null`/empty → no card.
//
// HONESTY (per memory: no "optimal"/"AI-recommended" claims): we say the data
// is UNDECIDED here and the user's knowledge resolves it. The from/to ordering
// is arbitrary — NOT a claimed direction — so each pair renders undirected
// (`from — to`) and the user explicitly PICKS which direction is causal.
//
// ORIENTATION CONTRACT (load-bearing): `edges_required` asserts only that the
// edge is PRESENT, not its direction — the backend pins presence to 1.0 but
// leaves direction data-driven. So `edges_required:[[A,B]]` alone does NOT
// orient A→B (verified on prod: edge stayed at the wrong direction). To pin an
// orientation we must ALSO forbid the reverse: when the user picks X→Y we emit
//   edges_required:  [{ src: X, dst: Y }]
//   edges_forbidden: [{ src: Y, dst: X }]   ← this is what actually orients it
// (verified: required[[X,Y]] + forbidden[[Y,X]] → direction_confidence=1.0).
//
// Each direction button reuses the exact KnowledgeNew prefill hand-off that
// SuggestedKnowledgeCard's "Customize first" uses: navigate('/knowledge/new',
// { state: { prefill: {...} } }). `datasetId` attaches the new knowledge to the
// right dataset, and `rerunDiscovery` keeps the save → re-run round-trip one
// click. KnowledgeNew already maps {src,dst} → [string,string] tuples for both
// edges_required and edges_forbidden at the prefill boundary.

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { Discovery, SuggestedLabel } from '@/lib/discoveries';
import { Compass } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Props {
  discovery: Discovery;
  // Variable names actually present in the result graph — a label is only
  // rendered when both endpoints reference real nodes (defensive against a
  // stale/garbled payload, mirroring SuggestedKnowledgeCard's node guard).
  resultNodes: string[];
}

function defaultKnowledgeName(datasetLabel: string): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate(),
  )} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const name = `${datasetLabel} · oriented edge · ${stamp}`;
  return name.length > 200 ? name.slice(0, 200) : name;
}

export function SuggestedLabelsCard({ discovery, resultNodes }: Props) {
  const navigate = useNavigate();

  const nodeSet = new Set(resultNodes);
  const labels = (discovery.suggested_labels ?? []).filter(
    (l): l is SuggestedLabel =>
      !!l &&
      typeof l.from === 'string' &&
      typeof l.to === 'string' &&
      l.from !== l.to &&
      nodeSet.has(l.from) &&
      nodeSet.has(l.to),
  );

  if (labels.length === 0) return null;

  const datasetLabel = discovery.dataset_name ?? discovery.dataset_id;

  // The user picks src→dst. We pin presence (edges_required) AND forbid the
  // reverse (edges_forbidden) — required alone only asserts presence, not
  // direction, so it would not orient the edge.
  function onPick(src: string, dst: string) {
    navigate('/knowledge/new', {
      state: {
        prefill: {
          datasetId: discovery.dataset_id,
          name: defaultKnowledgeName(datasetLabel),
          tiers: [],
          edges_required: [{ src, dst }],
          edges_forbidden: [{ src: dst, dst: src }],
          rerunDiscovery: {
            dataset_id: discovery.dataset_id,
            seed: discovery.params.seed ?? 42,
          },
        },
      },
    });
  }

  return (
    <section aria-labelledby="suggested-labels-title">
      <Card>
        <CardHeader>
          <CardTitle
            id="suggested-labels-title"
            className="flex items-center gap-2 text-base"
          >
            <Compass className="h-4 w-4" aria-hidden="true" />
            Resolve these first
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            The data couldn't decide these directions. Pick the correct one and
            we'll lock it in and re-run.
          </p>
          <ul className="space-y-3">
            {labels.map((label, idx) => (
              <li
                key={`${label.from}-${label.to}-${idx}`}
                className="flex flex-wrap items-start justify-between gap-3 rounded-md border bg-muted/20 px-3 py-2"
              >
                <div className="min-w-0 space-y-0.5">
                  <code className="font-mono text-sm">
                    {label.from} — {label.to}
                  </code>
                  <p className="text-xs text-muted-foreground">{label.reason}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onPick(label.from, label.to)}
                  >
                    {label.from} → {label.to}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onPick(label.to, label.from)}
                  >
                    {label.to} → {label.from}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
        <CardFooter>
          <p className="text-xs text-muted-foreground">
            The data is undecided here — your knowledge resolves it. You pick the
            direction; we re-run the discovery with it.
          </p>
        </CardFooter>
      </Card>
    </section>
  );
}

export default SuggestedLabelsCard;
